import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';

const MasterDashboard = () => {
  const [superAdmins, setSuperAdmins] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const navigate = useNavigate();
  const { logout } = useAuth();

  const handleLogout = () => {
    logout(); // Clears user from context/localStorage
    navigate('/'); // Redirect to login
  };

  const fetchSuperAdmins = async () => {
    try {
      const res = await api.get(`/api/master/super-admins`);
      setSuperAdmins(res.data);
    } catch (err) {
      console.error('Error fetching super admins:', err);
    }
  };

  useEffect(() => {
    fetchSuperAdmins();
  }, []);

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this Super Admin?')) {
      try {
        await api.delete(`/api/master/delete-super-admin/${id}`);
        fetchSuperAdmins();
      } catch (err) {
        console.error('Delete failed:', err);
      }
    }
  };

  const handleUpdate = async () => {
    try {
      await api.put(
        `/api/master/update-super-admin/${selectedAdmin._id}`,
        selectedAdmin
      );
      setOpenDialog(false);
      fetchSuperAdmins();
    } catch (err) {
      console.error('Update failed:', err);
      alert('Update failed');
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Button variant="outlined" color="error" onClick={handleLogout}>
        Logout
      </Button>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h5">Super Admins</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
          <Button
            variant="contained"
            onClick={() => navigate('/master/create-super-admin')}
          >
            Create Super Admin
          </Button>
          <Button
            variant="contained"
            onClick={() => navigate('/master/settings')}
          >
            Settings
          </Button>
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
            <TableRow>
              <TableCell>
                <strong>#</strong>
              </TableCell>
              <TableCell>
                <strong>Name</strong>
              </TableCell>
              <TableCell>
                <strong>Email</strong>
              </TableCell>
              <TableCell>
                <strong>Password</strong>
              </TableCell>
              <TableCell>
                <strong>Address</strong>
              </TableCell>
              <TableCell>
                <strong>City</strong>
              </TableCell>
              <TableCell>
                <strong>State</strong>
              </TableCell>
              <TableCell>
                <strong>Pincode</strong>
              </TableCell>
              <TableCell>
                <strong>Pincode Lock</strong>
              </TableCell>
              <TableCell>
                <strong>Actions</strong>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {superAdmins.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  No super admins found
                </TableCell>
              </TableRow>
            ) : (
              superAdmins.map((admin, index) => (
                <TableRow key={admin._id}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>{admin.name}</TableCell>
                  <TableCell>{admin.email}</TableCell>
                  <TableCell>{admin.password}</TableCell>
                  <TableCell>{admin.address}</TableCell>
                  <TableCell>{admin.city}</TableCell>
                  <TableCell>{admin.state}</TableCell>
                  <TableCell>{admin.pincode}</TableCell>
                  <TableCell>{admin.pincodeLock ? 'Yes' : 'No'}</TableCell>
                  <TableCell>
                    <IconButton
                      onClick={() => {
                        setSelectedAdmin(admin);
                        setOpenDialog(true);
                      }}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton onClick={() => handleDelete(admin._id)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Edit Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>Edit Super Admin</DialogTitle>
        <DialogContent
          sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}
        >
          <TextField
            label="Name"
            value={selectedAdmin?.name || ''}
            onChange={(e) =>
              setSelectedAdmin({ ...selectedAdmin, name: e.target.value })
            }
            fullWidth
          />
          <TextField
            label="Email"
            value={selectedAdmin?.email || ''}
            onChange={(e) =>
              setSelectedAdmin({ ...selectedAdmin, email: e.target.value })
            }
            fullWidth
          />
          <TextField
            label="Password"
            value={selectedAdmin?.password || ''}
            onChange={(e) =>
              setSelectedAdmin({ ...selectedAdmin, password: e.target.value })
            }
            fullWidth
          />
          <TextField
            label="Address"
            value={selectedAdmin?.address || ''}
            onChange={(e) =>
              setSelectedAdmin({ ...selectedAdmin, address: e.target.value })
            }
            fullWidth
          />
          <TextField
            label="City"
            value={selectedAdmin?.city || ''}
            onChange={(e) =>
              setSelectedAdmin({ ...selectedAdmin, city: e.target.value })
            }
            fullWidth
          />
          <TextField
            label="State"
            value={selectedAdmin?.state || ''}
            onChange={(e) =>
              setSelectedAdmin({ ...selectedAdmin, state: e.target.value })
            }
            fullWidth
          />
          <TextField
            label="Pincode"
            value={selectedAdmin?.pincode || ''}
            onChange={(e) =>
              setSelectedAdmin({ ...selectedAdmin, pincode: e.target.value })
            }
            fullWidth
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={selectedAdmin?.pincodeLock || false}
                onChange={(e) =>
                  setSelectedAdmin({
                    ...selectedAdmin,
                    pincodeLock: e.target.checked,
                  })
                }
              />
            }
            label="Pincode Lock"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdate}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MasterDashboard;
